
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: 'Meaning in life:',
    options: [
      'Fluctuates and must be nurtured',
      'Is fixed',
      'Depends on money',
      'Is only for young people',
      'Is a myth'
    ],
    correctAnswer: 0,
    explanation: 'Meaning in life fluctuates and must be nurtured.'
  },
  {
    question: 'Which practice helps maintain life satisfaction equilibrium?',
    options: [
      'Reflection',
      'Gratitude',
      'Purpose-tracking',
      'All of the above',
      'None of the above'
    ],
    correctAnswer: 3,
    explanation: 'Reflection, gratitude, and purpose-tracking all help maintain life satisfaction equilibrium.'
  },
  {
    question: 'Reframing challenges as growth opportunities is part of:',
    options: [
      'Designing your Life Fulfillment Plan',
      'Avoiding problems',
      'Ignoring values',
      'Seeking pleasure',
      'Making money'
    ],
    correctAnswer: 0,
    explanation: 'Reframing challenges as growth opportunities is part of designing your Life Fulfillment Plan.'
  },
  {
    question: 'A Meaning Maintenance Plan includes:',
    options: [
      'Daily gratitude journaling',
      'Weekly reflection',
      'Monthly satisfaction check',
      'All of the above',
      'None of the above'
    ],
    correctAnswer: 3,
    explanation: 'A Meaning Maintenance Plan includes daily gratitude journaling, weekly reflection, and monthly satisfaction check.'
  },
  {
    question: 'Who is the author and artist of your legacy?',
    options: [
      'Your parents',
      'Your boss',
      'You',
      'Society',
      'Friends'
    ],
    correctAnswer: 2,
    explanation: 'You are the author and artist of your legacy.'
  }
];

const LegendarialModule5QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('legendarialM5');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
  saveProgress(quizQuestions.length, true);
  setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(responses[prevQuestion] || null);
      setShowExplanation(responses[prevQuestion] !== undefined);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 5 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>Sustaining meaning and satisfaction</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Personal fulfillment strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Consistency in meaningful living</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by nurturing meaning and fulfillment!</p>
            <p>Ready to deepen your understanding of sustaining satisfaction?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/legendarial-resources')}
          >
            Return to Legendarial Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left legendarial-panel">
          <div className="module-header">
            <h1>Module 5 Quiz</h1>
            <p className="module-subtitle">Integrating Legendarial Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.<br/>
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuizQuestion.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuizQuestion.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuizQuestion.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuizQuestion.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/legendarial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegendarialModule5QuizPage;


